import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, OmniCampaign, Targetable, Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import {
  createTargeting,
  getTargetingByKind,
} from '@feathr/extender/components/Targetings/Targetings.utils';
import { useStore } from '@feathr/extender/state';
import { useDebouncedCallback, useToggle } from '@feathr/hooks';

import {
  createLookalikeTargetable,
  findArchivedLookalikeTargeting,
  findEnabledLookalikeTargeting,
  valueToSlider,
} from './LookalikeAudienceTargeting.utils';

interface IUseLookalikeTargetingProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

interface IUseLookalikeTargetingReturn {
  currentTargeting: Targeting | undefined;
  currentTargetable: Targetable | undefined;
  audienceSize: number | undefined;
}

/**
 * Finds and tracks the current lookalike targeting and its associated targetable.
 * Retrieves the targeting by kind and looks up the related targetable from the store.
 *
 * @param props.campaign - The campaign to find lookalike targeting for
 * @param props.targetings - Observable array of all targetings
 * @returns Object containing currentTargeting, currentTargetable, and audienceSize
 */
function useLookalikeTargeting({
  campaign,
  targetings,
}: IUseLookalikeTargetingProps): IUseLookalikeTargetingReturn {
  const { Targetables } = useStore();

  const currentTargeting = getTargetingByKind({
    campaign,
    kinds: ['omni_lookalike'],
    targetings,
  });

  const targetableId = currentTargeting?.get('target_data');
  const currentTargetable = targetableId ? Targetables.get(targetableId) : undefined;
  const audienceSize = currentTargetable?.get('audience_size') as number | undefined;

  return {
    currentTargeting,
    currentTargetable,
    audienceSize,
  };
}

interface IUseLookalikeToggleProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  currentTargeting: Targeting | undefined;
  onDisable?: () => void;
}

interface IUseLookalikeToggleReturn {
  isEnabled: boolean;
  handleToggleChange: (value?: boolean) => void;
}

/**
 * Manages the enabled/disabled state of lookalike targeting.
 * When enabled, creates a new targeting or reuses an existing archived one.
 * When disabled, archives the current targeting (and removes if ephemeral).
 *
 * @param props.campaign - The campaign this targeting belongs to
 * @param props.targetings - Observable array of all targetings
 * @param props.currentTargeting - The current lookalike targeting (if any)
 * @param props.onDisable - Optional callback when lookalike is disabled
 * @returns Object with isEnabled state and handleToggleChange function
 */
function useLookalikeToggle({
  campaign,
  targetings,
  currentTargeting,
  onDisable,
}: IUseLookalikeToggleProps): IUseLookalikeToggleReturn {
  const { Targetables, Targetings } = useStore();
  const [isEnabled, toggleIsEnabled] = useToggle(currentTargeting !== undefined);

  // Keep track of the current targeting so we can find it when disabling
  const [currentTargetingRef, setCurrentTargetingRef] = useState<Targeting | undefined>(
    currentTargeting,
  );

  function handleToggleChange(value?: boolean): void {
    toggleIsEnabled();

    // If toggle is enabled, reuse existing targeting or create a new one
    if (value) {
      // Recompute enabled targeting to get current state
      const currentEnabledTargeting = findEnabledLookalikeTargeting(targetings, campaign.get('id'));

      // If there's already an enabled targeting, nothing to do
      if (currentEnabledTargeting) {
        // Update the ref to track this targeting
        setCurrentTargetingRef(currentEnabledTargeting);
        return;
      }

      // Check for archived targeting to reuse instead of creating a new one
      const existingArchivedTargeting = findArchivedLookalikeTargeting(
        targetings,
        campaign.get('id'),
      );

      if (existingArchivedTargeting) {
        // If the targeting has an ID, it was saved to the backend
        // If it's archived in the backend, we can't unarchive it (backend filters it out)
        // So we should create a new one instead
        if (existingArchivedTargeting.id && !existingArchivedTargeting.isEphemeral) {
          // This targeting was saved to the backend and is archived
          // The backend can't find archived targetings to update them, so create a new one
          const newTargetable = createLookalikeTargetable(Targetables);
          const newTargeting = createTargeting({
            campaign,
            Targetings,
            targetableId: newTargetable.id,
            kind: 'omni_lookalike',
            targetings,
          });
          setCurrentTargetingRef(newTargeting);
          return;
        }

        if (existingArchivedTargeting.isEphemeral) {
          const targetableId = existingArchivedTargeting.get('target_data');
          const existingTargetable = targetableId ? Targetables.get(targetableId) : undefined;

          if (!existingTargetable && targetableId) {
            const newTargetable = createLookalikeTargetable(Targetables);
            runInAction(() => {
              existingArchivedTargeting.set({
                is_archived: false,
                target_data: newTargetable.id,
              });
            });
          } else {
            runInAction(() => {
              existingArchivedTargeting.set({ is_archived: false });
            });
          }
          setCurrentTargetingRef(existingArchivedTargeting);
          return;
        }

        const newTargetable = createLookalikeTargetable(Targetables);
        const newTargeting = createTargeting({
          campaign,
          Targetings,
          targetableId: newTargetable.id,
          kind: 'omni_lookalike',
          targetings,
        });
        setCurrentTargetingRef(newTargeting);
        return;
      }

      const newTargetable = createLookalikeTargetable(Targetables);
      const newTargeting = createTargeting({
        campaign,
        Targetings,
        targetableId: newTargetable.id,
        kind: 'omni_lookalike',
        targetings,
      });
      setCurrentTargetingRef(newTargeting);
    } else if (value === false) {
      const currentEnabledTargeting =
        currentTargetingRef || findEnabledLookalikeTargeting(targetings, campaign.get('id'));

      if (currentEnabledTargeting) {
        onDisable?.();
        const isEphemeral = currentEnabledTargeting.isEphemeral;
        runInAction(() => {
          currentEnabledTargeting.set({ is_archived: true });
          setCurrentTargetingRef(undefined);
        });

        if (isEphemeral) {
          queueMicrotask(() => {
            runInAction(() => {
              targetings.remove(currentEnabledTargeting);
              Targetings.remove(currentEnabledTargeting.id);
            });
          });
        }
      }
    }
  }

  return {
    isEnabled,
    handleToggleChange,
  };
}

interface IUseLookalikeSliderProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  audienceSize: number | undefined;
}

interface IUseLookalikeSliderReturn {
  localAudienceSize: number | undefined;
  handleSliderChange: (value: number) => void;
  resetLocalAudienceSize: () => void;
}

/**
 * Manages the slider state for lookalike audience size.
 * Maintains local state for immediate UI feedback while debouncing
 * updates to the targetable to avoid excessive store mutations.
 *
 * @param props.campaign - The campaign this targeting belongs to
 * @param props.targetings - Observable array of all targetings
 * @param props.audienceSize - The current audience size from the targetable
 * @returns Object with localAudienceSize, handleSliderChange, and resetLocalAudienceSize
 */
function useLookalikeSlider({
  campaign,
  targetings,
  audienceSize,
}: IUseLookalikeSliderProps): IUseLookalikeSliderReturn {
  const { Targetables } = useStore();
  const [localAudienceSize, setLocalAudienceSize] = useState<number | undefined>(audienceSize);

  useEffect(() => {
    setLocalAudienceSize(audienceSize);
  }, [audienceSize]);

  const updateTargetableAudienceSize = useCallback(
    (value: number): void => {
      const currentTargetingForUpdate = findEnabledLookalikeTargeting(
        targetings,
        campaign.get('id'),
      );

      if (!currentTargetingForUpdate) {
        return;
      }

      const currentTargetableId = currentTargetingForUpdate.get('target_data');
      if (!currentTargetableId) {
        const sliderPosition = valueToSlider(value);
        const newTargetable = createLookalikeTargetable(Targetables, sliderPosition);

        runInAction(() => {
          currentTargetingForUpdate.set({
            target_data: newTargetable.id,
          });
        });
        return;
      }

      const currentTargetableForUpdate = Targetables.get(currentTargetableId);

      if (!currentTargetableForUpdate) {
        const sliderPosition = valueToSlider(value);
        const newTargetable = createLookalikeTargetable(Targetables, sliderPosition);

        runInAction(() => {
          currentTargetingForUpdate.set({
            target_data: newTargetable.id,
          });
        });
        return;
      }

      runInAction(() => {
        currentTargetableForUpdate.set({
          audience_size: value,
        });
      });
    },
    [campaign, targetings, Targetables],
  );

  const debouncedUpdateTargetable = useDebouncedCallback(updateTargetableAudienceSize, 600, [
    campaign,
    targetings,
    Targetables,
  ]);

  function handleSliderChange(value: number): void {
    setLocalAudienceSize(value);
    debouncedUpdateTargetable(value);
  }

  function resetLocalAudienceSize(): void {
    setLocalAudienceSize(0);
  }

  return {
    localAudienceSize,
    handleSliderChange,
    resetLocalAudienceSize,
  };
}

interface IUseLookalikeAutoArchiveProps {
  isEnabled: boolean;
  isLoading: boolean;
  numReachableUsers: number;
  minReachableUsers: number;
  campaign: OmniCampaign;
  onArchive: () => void;
}

/**
 * Automatically archives lookalike targeting when it becomes incompatible with the
 * current campaign configuration:
 * - Reachable users drops below the minimum (after valid data has been seen, to avoid
 *   false positives during page load).
 * - A Meta special ad category is set, since Meta's API rejects lookalikes in any
 *   special ad category.
 *
 * Shows a toast notification explaining why the lookalike was removed.
 *
 * @param props.isEnabled - Whether lookalike targeting is currently enabled
 * @param props.isLoading - Whether audience data is still loading
 * @param props.numReachableUsers - Current number of reachable users from selected groups
 * @param props.minReachableUsers - Minimum required reachable users (5000 normal, 100 internal)
 * @param props.campaign - The Omni campaign; its `hasSpecialAdCategory` getter is read here
 * @param props.onArchive - Callback to archive the lookalike targeting
 */
function useLookalikeAutoArchive({
  isEnabled,
  isLoading,
  numReachableUsers,
  minReachableUsers,
  campaign,
  onArchive,
}: IUseLookalikeAutoArchiveProps): void {
  const { t } = useTranslation();
  const hasShownToastRef = useRef(false);
  const hasShownSpecialAdCategoryToastRef = useRef(false);
  const hasSeenValidDataRef = useRef(false);
  const hasSpecialAdCategory = campaign.hasSpecialAdCategory;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (numReachableUsers >= minReachableUsers) {
      hasSeenValidDataRef.current = true;
      hasShownToastRef.current = false;
    }

    if (!hasSpecialAdCategory) {
      hasShownSpecialAdCategoryToastRef.current = false;
    }

    if (isEnabled && hasSpecialAdCategory) {
      onArchive();
      if (!hasShownSpecialAdCategoryToastRef.current) {
        toast(t('Lookalike audience was removed because a Meta special ad category is set.'), {
          type: 'info',
        });
        hasShownSpecialAdCategoryToastRef.current = true;
      }
      return;
    }

    if (isEnabled && hasSeenValidDataRef.current && numReachableUsers < minReachableUsers) {
      onArchive();
      if (!hasShownToastRef.current) {
        toast(
          t(
            'Lookalike audience was removed because there are not enough reachable people in the selected groups.',
          ),
          { type: 'info' },
        );
        hasShownToastRef.current = true;
      }
    }
  }, [
    numReachableUsers,
    minReachableUsers,
    isEnabled,
    isLoading,
    hasSpecialAdCategory,
    onArchive,
    t,
  ]);
}

interface IUseLookalikeAudienceProps {
  targetings: IObservableArray<Targeting>;
  campaign: Campaign;
  numReachableUsers: number;
  minReachableUsers: number;
  isLoading: boolean;
}

interface IUseLookalikeAudienceReturn {
  handleSliderChange: (value: number) => void;
  handleToggleChange: (value?: boolean) => void;
  isEnabled: boolean;
  audienceSize: number | undefined;
  hasSpecialAdCategory: boolean;
}

/**
 * Main hook for lookalike audience targeting functionality.
 *
 * Composes multiple focused hooks into a unified interface:
 * - useLookalikeTargeting: Finds and tracks the current targeting/targetable
 * - useLookalikeSlider: Manages audience size slider with debounced updates
 * - useLookalikeToggle: Handles enable/disable with targeting lifecycle
 * - useLookalikeAutoArchive: Auto-disables when audience drops below threshold
 *
 * @param props.targetings - Observable array of all targetings
 * @param props.campaign - The campaign this targeting belongs to
 * @param props.numReachableUsers - Current number of reachable users from selected groups
 * @param props.minReachableUsers - Minimum required reachable users for lookalike
 * @param props.isLoading - Whether audience data is still loading
 * @returns Object with isEnabled, audienceSize, handleSliderChange, and handleToggleChange
 */
function useLookalikeAudience({
  targetings,
  campaign,
  numReachableUsers,
  minReachableUsers,
  isLoading,
}: IUseLookalikeAudienceProps): IUseLookalikeAudienceReturn {
  // Component is only rendered for Omni campaigns; see AdWizardAudienceStepContent.
  const omniCampaign = campaign as OmniCampaign;

  const { currentTargeting, audienceSize } = useLookalikeTargeting({
    campaign,
    targetings,
  });

  const { localAudienceSize, handleSliderChange, resetLocalAudienceSize } = useLookalikeSlider({
    campaign,
    targetings,
    audienceSize,
  });

  const { isEnabled, handleToggleChange } = useLookalikeToggle({
    campaign,
    targetings,
    currentTargeting,
    onDisable: resetLocalAudienceSize,
  });

  useLookalikeAutoArchive({
    isEnabled,
    isLoading,
    numReachableUsers,
    minReachableUsers,
    campaign: omniCampaign,
    onArchive: () => handleToggleChange(false),
  });

  return {
    isEnabled,
    handleSliderChange,
    handleToggleChange,
    audienceSize: localAudienceSize ?? audienceSize,
    hasSpecialAdCategory: omniCampaign.hasSpecialAdCategory,
  };
}

export { useLookalikeAudience, useLookalikeAutoArchive };
